import { create } from 'zustand';
import { createEmptyProject, ProjectSchema, type NumberNodeProject, type VisualSpec } from '@numbernode/contracts';
import type { QueryResult } from '../engine/client';

export type Workspace = 'data' | 'prepare' | 'model' | 'explore' | 'dashboard' | 'notebook' | 'share';

type HistoryEntry = { label: string; project: NumberNodeProject };
type StudioState = {
  project: NumberNodeProject;
  activeWorkspace: Workspace;
  selectedSourceId?: string;
  selectedVisualId?: string;
  queryResult?: QueryResult;
  busy: boolean;
  engineLabel: string;
  error?: string;
  past: HistoryEntry[];
  future: HistoryEntry[];
  setProject(project: NumberNodeProject, clearHistory?: boolean): void;
  mutate(label: string, updater: (project: NumberNodeProject) => void): void;
  undo(): void;
  redo(): void;
  setWorkspace(workspace: Workspace): void;
  setSelectedSource(id?: string): void;
  setSelectedVisual(id?: string): void;
  setQueryResult(result?: QueryResult): void;
  setBusy(busy: boolean): void;
  setEngineLabel(label: string): void;
  setError(error?: string): void;
  currentVisual(): VisualSpec | undefined;
};

function cloneProject(project: NumberNodeProject): NumberNodeProject {
  return structuredClone(project);
}

export const useProjectStore = create<StudioState>((set, get) => ({
  project: createEmptyProject(),
  activeWorkspace: 'data',
  busy: false,
  engineLabel: 'Starting local engine…',
  past: [],
  future: [],
  setProject(project, clearHistory = true) {
    set({ project: ProjectSchema.parse(project), past: clearHistory ? [] : get().past, future: clearHistory ? [] : get().future });
  },
  mutate(label, updater) {
    const before = cloneProject(get().project);
    const next = cloneProject(before);
    updater(next);
    next.updatedAt = new Date().toISOString();
    const parsed = ProjectSchema.parse(next);
    set({ project: parsed, past: [...get().past.slice(-49), { label, project: before }], future: [] });
  },
  undo() {
    const past = get().past;
    const entry = past.at(-1);
    if (!entry) return;
    set({ project: cloneProject(entry.project), past: past.slice(0, -1), future: [{ label: entry.label, project: cloneProject(get().project) }, ...get().future].slice(0, 50) });
  },
  redo() {
    const [entry, ...future] = get().future;
    if (!entry) return;
    set({ project: cloneProject(entry.project), past: [...get().past, { label: entry.label, project: cloneProject(get().project) }].slice(-50), future });
  },
  setWorkspace(activeWorkspace) { set({ activeWorkspace }); },
  setSelectedSource(selectedSourceId) { set({ selectedSourceId }); },
  setSelectedVisual(selectedVisualId) { set({ selectedVisualId }); },
  setQueryResult(queryResult) { set({ queryResult }); },
  setBusy(busy) { set({ busy }); },
  setEngineLabel(engineLabel) { set({ engineLabel }); },
  setError(error) { set({ error }); },
  currentVisual() { return get().project.visuals.find((visual) => visual.id === get().selectedVisualId); },
}));
